import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone } from "lucide-react";
import { api } from "@/lib/api";

const PLATFORM_LABELS = {
  facebook: "Facebook", x: "X", threads: "Threads", linkedin: "LinkedIn",
  discord: "Discord", telegram: "Telegram", bluesky: "Bluesky",
};

export default function EngAnnouncements() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/engagement/rewards/announcements").then((r) => setCfg(r.data));
  }, []);
  if (!cfg) return null;

  const save = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    try {
      await api.put("/engagement/rewards/announcements", {
        enabled: next.enabled, min_points: Number(next.min_points) || 1,
        platforms: next.platforms, template: next.template });
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const togglePlatform = (p) => {
    const on = cfg.platforms.includes(p);
    save({ platforms: on ? cfg.platforms.filter((x) => x !== p) : [...cfg.platforms, p] });
  };

  return (
    <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-3" data-testid="eng-announcements">
      <label className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-bold"><Megaphone className="h-4 w-4 text-brand-cyan" /> Reward shout-outs on socials</span>
        <input type="checkbox" checked={!!cfg.enabled} data-testid="eng-announce-toggle"
          onChange={(e) => save({ enabled: e.target.checked })} />
      </label>
      <p className="text-[11px] text-ink-muted -mt-1">When you approve a big redemption, a shout-out post is queued to your connected socials automatically. Only the listener's first name is shared.</p>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Announce rewards worth</span>
        <input type="number" min={1} value={cfg.min_points} data-testid="eng-announce-min-points"
          onChange={(e) => save({ min_points: e.target.value })}
          className="w-24 rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-sm" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">points or more</span>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {(cfg.supported_platforms || []).map((p) => {
          const connected = (cfg.connected_platforms || []).includes(p);
          const on = cfg.platforms.includes(p);
          return (
            <button key={p} onClick={() => connected && togglePlatform(p)} disabled={!connected}
              data-testid={`eng-announce-platform-${p}`}
              title={connected ? "" : "Connect this platform in Settings → Social Connections first"}
              className={`rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                !connected ? "border-line/50 text-ink-muted/50 cursor-not-allowed"
                : on ? "bg-brand-cyan/15 border-brand-cyan/50 text-brand-cyan"
                : "border-line text-ink-secondary hover:text-ink-primary"}`}>
              {PLATFORM_LABELS[p] || p}{!connected ? " ·" : on ? " ✓" : ""}
            </button>
          );
        })}
      </div>

      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Post template</div>
        <textarea rows={3} value={cfg.template} data-testid="eng-announce-template"
          onChange={(e) => setCfg((c) => ({ ...c, template: e.target.value }))}
          onBlur={() => save({})}
          className="w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm" maxLength={500} />
        <div className="text-[10px] text-ink-muted">Placeholders: <span className="font-mono text-brand-cyan">{"{listener}"}</span> <span className="font-mono text-brand-cyan">{"{reward}"}</span> <span className="font-mono text-brand-cyan">{"{points}"}</span> <span className="font-mono text-brand-cyan">{"{station}"}</span></div>
      </div>
    </div>
  );
}
